import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Chip, IconButton, Stack, Collapse, Button, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { starAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';

const TYPE_META = {
  project: { label: 'Project', color: '#1565C0' },
  defect: { label: 'Defect', color: '#C62828' },
  task: { label: 'Task', color: '#2E7D32' },
  ticket: { label: 'Ticket', color: '#6A1B9A' },
};
const DONE = ['completed', 'cancelled', 'closed', 'resolved'];
const PREVIEW_COUNT = 3;
const REMIND_EVERY_MS = 30 * 60 * 1000; /* the popup comes back every 30 minutes */

/* Open the right page for an item (tickets open their dialog via router state). */
export const openTatItem = (navigate, i) => {
  if (i.type === 'project') navigate('/projects/' + i.id);
  else if (i.type === 'task') navigate('/tasks/' + i.id);
  else if (i.type === 'ticket') navigate('/tickets', { state: { openTicket: { projectId: i.project_id, ticketId: i.id } } });
  else navigate('/defects');
};

const safeGet = (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } };
const safeSet = (k, v) => { try { window.localStorage.setItem(k, v); } catch (e) { /* ignore */ } };

/* Bottom-right reminder: projects, defects and tasks whose TAT date is today. */
export default function TatPopup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState({ date: '', items: [] });
  const dismissKey = user ? `tatPopupDismissedAt:${user.id}` : '';
  const stillDismissed = () => {
    const at = parseInt(safeGet(dismissKey) || '0', 10);
    return !!at && Date.now() - at < REMIND_EVERY_MS;
  };
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissKey) return false;
    /* A new login = a new token: forget any earlier snooze so the popup shows right after login. */
    const tokenKey = `tatPopupToken:${user.id}`;
    const token = safeGet('token');
    if (safeGet(tokenKey) !== token) {
      safeSet(tokenKey, token || '');
      try { window.localStorage.removeItem(dismissKey); } catch (e) { /* ignore */ }
      return false;
    }
    return stillDismissed();
  });
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    starAPI.tatToday()
      .then((res) => {
        setData(res.data || { date: '', items: [] });
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    load();
    /* refresh the list every 5 minutes */
    const refresh = setInterval(load, 5 * 60 * 1000);
    /* once a minute check whether the 30-minute snooze is over, then pop up again */
    const tick = setInterval(() => {
      if (!stillDismissed()) {
        setDismissed((was) => {
          if (was) { setCollapsed(false); load(); }
          return false;
        });
      }
    }, 60 * 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
    // eslint-disable-next-line
  }, [load]);

  const items = data.items || [];
  if (!user || dismissed || items.length === 0) return null;

  const overdueCount = items.filter((i) => i.overdue).length;
  const todayPending = items.filter((i) => !i.overdue && !DONE.includes(i.status)).length;
  /* overdue first (oldest TAT first), then due today pending, finished last */
  const rank = (i) => (i.overdue ? 0 : DONE.includes(i.status) ? 2 : 1);
  const sorted = [...items].sort((a, b) => rank(a) - rank(b) || String(a.tat_date).localeCompare(String(b.tat_date)));
  const preview = sorted.slice(0, PREVIEW_COUNT);

  const dismiss = () => {
    safeSet(dismissKey, String(Date.now()));
    setDismissed(true);
  };

  return (
    <Paper elevation={8}
      sx={{ position: 'fixed', right: 16, bottom: isMobile ? 76 : 16, width: { xs: 'calc(100% - 32px)', sm: 360 }, maxWidth: 400,
        zIndex: 1400, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'warning.light' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: '#FFF4E5' }}>
        <EventBusyIcon sx={{ color: '#E65100' }} fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2, color: overdueCount ? 'error.main' : 'text.primary' }}>
            {overdueCount ? 'TAT date passed / due' : 'TAT date is today'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {overdueCount ? `${overdueCount} overdue · ` : ''}{todayPending} due today · {items.length} total
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={dismiss} title="Hide — reminds again in 30 minutes"><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />} sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {preview.map((i) => {
            const meta = TYPE_META[i.type];
            const done = DONE.includes(i.status);
            return (
              <Box key={`${i.type}-${i.id}`} onClick={() => openTatItem(navigate, i)}
                sx={{ px: 1.5, py: 1, cursor: 'pointer', opacity: done ? 0.6 : 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                  <Chip label={meta.label} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: meta.color + '18', color: meta.color }} />
                  <Chip label={(i.status || '').replace('_', ' ')} size="small" variant="outlined"
                    color={done ? 'success' : 'warning'} sx={{ height: 18, fontSize: '0.62rem', textTransform: 'capitalize' }} />
                  {i.overdue && <Chip label={`Overdue · TAT ${i.tat_date}`} size="small" color="error" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }} />}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{i.name}</Typography>
                {i.project_name && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{i.project_name}</Typography>}
              </Box>
            );
          })}
        </Stack>
        <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Button size="small" fullWidth onClick={() => navigate('/tat')} sx={{ fontWeight: 700 }}>
            Show more{items.length > PREVIEW_COUNT ? ` (${items.length - PREVIEW_COUNT} more)` : ''}
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
}